import React, {useLayoutEffect, useMemo, useState} from 'react';
import {classes, style} from './doppe-action-type-youtube-video-widget.st.css';
import {DoppeActionTypeYoutubeVideo, YoutubeEmbedType} from '../doppe-action-type-youtube-video';
import {
    doppeValidateYoutubeShortURL,
    evaluateFunction,
    urlCreateQueryString,
    VideoServices,
    videoServicesResolveVideoSource
} from '@wix/devzai-utils-common';
import {RatioMaintainer} from '@wix/devzai-utils-react';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeHideableValueIsVisibleAndNotEqualValue} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    BymoPageViewerMode,
    useBymoPageViewerContext
} from '../../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {doppeDtoActionViewTypeIsDisplayedAsButton} from '../../../client-server-common/types/doppe-dto-action';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {IDoppeViewerActionContext} from '../../../doppe-sdk';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeYoutubeVideoWidget = React.memo(function DoppeActionTypeYoutubeVideoWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeYoutubeVideo.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const viewType = actionContext.getActionViewType();
    const pageViewerContext = useBymoPageViewerContext();
    const actionSettings = actionContext.action.settings;


    const {
        muteVideo,
        autoplay
    } = evaluateFunction(() => {

        if (pageViewerContext.mode === BymoPageViewerMode.Preview) {
            return {
                muteVideo: true,
                autoplay: false
            }
        } else if (doppeDtoActionViewTypeIsDisplayedAsButton(viewType)) {
            return {
                muteVideo: false,
                autoplay: true
            }
        } else {

            const mutedAutoplay = actionSettings.videoPlayerSettings.autoplay;

            return {
                muteVideo: mutedAutoplay,
                autoplay: mutedAutoplay
            }
        }
    })

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeYoutubeVideoWidgetInner
                actionContext={actionContext}
                reportGoalAchieved={goalType => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    })
                }}
                actionId={actionContext.action.id}
                actionSettings={{
                    ...actionContext.action.settings,
                    videoPlayerSettings: {
                        ...actionContext.action.settings.videoPlayerSettings,
                        autoplay: autoplay,
                        muteVideo: muteVideo
                    }
                }}
                videoTitle={doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.title, '') ? actionSettings.title : actionContext.action.name}
            />
        </DoppeActionTypeWidgetLayout>
    );
});


export const DoppeActionTypeYoutubeVideoWidgetInner = React.memo(function DoppeActionTypeYoutubeVideoWidgetInner(
    props: {
        actionSettings: DoppeActionTypeYoutubeVideo.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        videoTitle?: string;
        actionContext: IDoppeViewerActionContext;
    }
) {

    const {
        actionSettings,
        reportGoalAchieved,
        videoTitle,
        actionContext
    } = props;

    const actionTitle = videoTitle;

    const {
        description,
        title,
        url,
        videoPlayerSettings: {
            autoplay,
            muteVideo,
        },
        ctaButtons
    } = actionSettings;

    const type = actionSettings.type;
    const videoServiceSource = useMemo(() => {
        return type === YoutubeEmbedType.VideoUrl ? videoServicesResolveVideoSource(url) : {
            videoService: VideoServices.YoutubeChannelVideos,
            videoId: url,
        };
    }, [url, type])

    const [hostname, setHostname] = useState<string>();
    useLayoutEffect(() => {
        setHostname(window.location.hostname)
    }, [])

    const showInnerContent =
        doppeHideableValueIsVisibleAndNotEqualValue(title, '') ||
        doppeHideableValueIsVisibleAndNotEqualValue(description, '') ||
        ctaButtons.filter(ctaButton => ctaButton.isVisible).length > 0;

    const normalizedVideoURL = videoServiceSource?.videoService === VideoServices.YoutubeChannelVideos ? `https://www.youtube.com/channel/${url}` : url;

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
            actionContext={actionContext}
            reportGoalAchieved={reportGoalAchieved}
            fullWidthMainMedia={true}
            mainMedia={<div
                className={style(classes.videoContent, {showInnerContent: showInnerContent})}
            >
                {evaluateFunction(() => {

                    if (videoServiceSource) {
                        switch (videoServiceSource.videoService) {
                            case VideoServices.Youtube: {

                                const videoId = videoServiceSource.videoId;
                                const isYoutubeShort = doppeValidateYoutubeShortURL(url);
                                const ratio = isYoutubeShort ? (453 /804) : (16 / 9);

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={ratio}
                                    >
                                        <iframe
                                            src={`https://www.youtube.com/embed/${videoId}?${urlCreateQueryString({
                                                autoplay: autoplay ? '1' : '0',
                                                mute: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                modestbranding: '1',
                                                origin: hostname,
                                                iv_load_policy: '3',
                                                rel: '0',
                                                start: '0',
                                                // cc_load_policy: '0',
                                                // fs: '0'
                                            }, false)}`}
                                            allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                                            frameBorder="0"
                                            title={actionTitle}
                                        />
                                    </RatioMaintainer>
                                )
                            }
                            case VideoServices.YoutubePlaylist: {

                                const videoId = videoServiceSource.videoId;

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={1}
                                    >
                                        <iframe
                                            src={`https://www.youtube.com/embed/videoseries?${urlCreateQueryString({
                                                list: videoId,
                                                autoplay: autoplay ? '1' : '0',
                                                mute: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                modestbranding: '1',
                                                origin: hostname,
                                                iv_load_policy: '3',
                                                rel: '0',
                                                start: '0',
                                                // cc_load_policy: '0',
                                                // fs: '0'
                                            }, false)}`}
                                            title={actionTitle}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen>
                                        </iframe>
                                    </RatioMaintainer>
                                )
                            }
                            case VideoServices.YoutubeChannelVideos: {

                                const videoId = videoServiceSource.videoId;

                                return (
                                    <RatioMaintainer
                                        widthHeightRatio={1}
                                    >
                                        <iframe
                                            src={`https://www.youtube.com/embed?${urlCreateQueryString({
                                                list: 'UU' + videoId.slice(2),
                                                listType: 'playlist',
                                                autoplay: autoplay ? '1' : '0',
                                                mute: muteVideo ? '1' : '0',
                                                playsinline: '1',
                                                modestbranding: '1',
                                                origin: hostname,
                                                iv_load_policy: '3',
                                                rel: '0',
                                                start: '0',
                                            }, false)}`}
                                            title={actionTitle}
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen>
                                        </iframe>
                                    </RatioMaintainer>
                                )
                            }
                        }
                    }

                    return (
                        <RatioMaintainer
                            widthHeightRatio={16 / 9}
                        >
                            <div className={style(classes.notSupportedVideo, {})}>Not Supported Video</div>
                        </RatioMaintainer>
                    )
                })}
            </div>}
            sections={actionSettings.sections}
            buttons={actionSettings.ctaButtons}
            buttonsSettings={{
                sourceURL: normalizedVideoURL,
            }}
        >
            <DoppeActionTypeWidgetInnerTitle>
                {title}
            </DoppeActionTypeWidgetInnerTitle>
            <DoppeActionTypeWidgetInnerDescription>
                {description}
            </DoppeActionTypeWidgetInnerDescription>
        </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
    );
});
